import { useState } from "react";
import {
  BookBookmark,
  FileText,
  Package,
  PencilLine,
  UsersThree,
} from "@phosphor-icons/react";
import { t } from "i18next";
import { useLocation, useNavigate } from "react-router-dom";

import {
  AccessRequests,
  ApprovedUsers,
  Bundles,
  Courses,
  Materials,
} from "@/components/Materials";
import { useBundles } from "@/hooks/useBundles";
import { ContentLayout } from "@/layout";
import { ROUTES } from "@/router";
import { useUserStore } from "@/stores";
import type { Bundle } from "@/types";
import type { Tab } from "@/ui";
import { Breadcrumb, Heading, MultiSelect, SectionHeader, Tabs } from "@/ui";
import { convertToOptions } from "@/utils";

export const CoursesAndMaterials = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUserStore();
  const [selectedBundles, setSelectedBundles] = useState<string[]>([]);

  const { useAllBundles } = useBundles();
  const { data: bundlesData } = useAllBundles();
  const bundlesOptions = convertToOptions<Bundle>(bundlesData?.data ?? []);

  const tabs: [Tab, ...Tab[]] = [
    {
      component: <ApprovedUsers selectedBundles={selectedBundles} />,
      label: t("materials.approved_users"),
      roles: ["admin"],
      icon: <UsersThree />,
    },
    {
      component: <AccessRequests selectedBundles={selectedBundles} />,
      label: t("materials.access_requests"),
      roles: ["admin", "partner"],
      icon: <PencilLine />,
    },
    {
      component: <Materials />,
      label: t("materials.materials"),
      roles: ["admin", "partner"],
      icon: <FileText />,
    },
    {
      component: <Bundles />,
      label: "Bundles",
      roles: ["admin", "partner"],
      icon: <Package />,
    },
    {
      component: <Courses />,
      label: "Courses",
      roles: ["admin", "partner"],
      icon: <BookBookmark />,
    },
  ];

  // The dashboard's "Needs attention" card links straight to Access requests.
  const openAccessRequests =
    (location.state as { tab?: string } | null)?.tab === "access-requests";
  const [currentTabLabel, setCurrentTabLabel] = useState(
    openAccessRequests ? t("materials.access_requests") : tabs[0].label,
  );

  const breadcrumbItems = [
    {
      label: t("general.courses_and_materials"),
      href: ROUTES.coursesAndMaterials,
      icon: <BookBookmark />,
    },
  ];

  return (
    <ContentLayout className="flex flex-col gap-y-6">
      <Breadcrumb
        items={breadcrumbItems}
        current={t("general.courses_and_materials")}
        onItemClick={(item) => {
          if (item.href) {
            navigate(item.href);
          }
        }}
      />
      <SectionHeader
        left={
          <Heading
            level={2}
            heading={t("general.courses_and_materials")}
            className="text-3xl"
          />
        }
        right={
          <MultiSelect
            placeholder={t("materials.select_course")}
            options={bundlesOptions}
            selectedOptions={selectedBundles}
            onChange={(newSelectedBundles) => {
              setSelectedBundles(newSelectedBundles);
            }}
            containerClassName="w-full sm:w-[320px]"
          />
        }
      />
      <Tabs
        tabs={tabs}
        userRole={user?.role ?? "loggedOut"}
        currentLabel={currentTabLabel}
        setCurrentLabel={setCurrentTabLabel}
      />
    </ContentLayout>
  );
};
